/**
 * Сроки и периоды — те же формулы, что на бэкенде (`apps/api/src/domain/deadlines.ts`)
 * и в макете (`dateClass`, `leftText`, `computeRange`).
 *
 * Правила, которые нельзя менять:
 *  - «горит» = `0 <= осталось <= 3` календарных дня, обе границы включительно;
 *  - «просрочено» = `осталось < 0`; дело со сроком «сегодня» ещё не просрочено;
 *  - выходные НЕ пропускаются: перенос с выходных мини делает один раз при заведении дела;
 *  - предыдущий период — той же длины, впритык перед текущим, без зазора.
 */

import type { DateLike } from './format';
import { dayWord, fmtDate, fmtShort, wallClock } from './format';

export const MS_DAY = 86_400_000;
export const SOON_DAYS = 3;
export const DEFAULT_TZ = 'Europe/Moscow';

/** Индекс = `Date.getDay()` (макет, справочник DOW). */
export const DOW_SHORT = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'] as const;

let displayTimeZone = DEFAULT_TZ;

/** Ставится один раз из `MeDto.clinic.timezone` (см. AuthProvider). */
export function setDisplayTimeZone(tz: string | null | undefined): void {
    if (tz) displayTimeZone = tz;
}

export function getDisplayTimeZone(): string {
    return displayTimeZone;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function isoFormatter(tz: string): Intl.DateTimeFormat {
    let f = formatters.get(tz);
    if (!f) {
        f = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
        formatters.set(tz, f);
    }
    return f;
}

/** Сегодняшняя дата в таймзоне клиники, `ГГГГ-ММ-ДД`. */
export function todayIso(tz: string = displayTimeZone): string {
    const parts = isoFormatter(tz).formatToParts(new Date());
    let year = '';
    let month = '';
    let day = '';
    for (const part of parts) {
        if (part.type === 'year') year = part.value;
        else if (part.type === 'month') month = part.value;
        else if (part.type === 'day') day = part.value;
    }
    return year + '-' + month + '-' + day;
}

/** Номер календарного дня (дней от 1970-01-01) — точная разница дат без TZ-арифметики. */
export function dayNumber(value: DateLike): number | null {
    const w = wallClock(value);
    if (!w) return null;
    return Math.floor(Date.UTC(w.year, w.month - 1, w.day) / MS_DAY);
}

export function todayDayNumber(tz: string = displayTimeZone): number {
    return dayNumber(todayIso(tz)) as number;
}

/** `ГГГГ-ММ-ДД` по номеру дня. */
export function isoFromDayNumber(dn: number): string {
    const d = new Date(dn * MS_DAY);
    const month = d.getUTCMonth() + 1;
    const day = d.getUTCDate();
    const mm = month < 10 ? '0' + month : String(month);
    const dd = day < 10 ? '0' + day : String(day);
    return d.getUTCFullYear() + '-' + mm + '-' + dd;
}

/** Сдвиг ISO-даты на N календарных дней. */
export function shiftIso(iso: string, days: number): string {
    const dn = dayNumber(iso);
    if (dn === null) return iso;
    return isoFromDayNumber(dn + days);
}

/** Разница в календарных днях: `b - a`. Выходные не пропускаются. */
export function daysBetween(a: DateLike, b: DateLike): number | null {
    const from = dayNumber(a);
    const to = dayNumber(b);
    if (from === null || to === null) return null;
    return to - from;
}

/** День недели по номеру дня: 0 — воскресенье (1970-01-01 — четверг). */
export function dayOfWeek(dn: number): number {
    return (((dn + 4) % 7) + 7) % 7;
}

export function isWeekendIso(iso: string): boolean {
    const dn = dayNumber(iso);
    if (dn === null) return false;
    const dow = dayOfWeek(dn);
    return dow === 0 || dow === 6;
}

export function dowShort(iso: string): string {
    const dn = dayNumber(iso);
    if (dn === null) return '';
    return DOW_SHORT[dayOfWeek(dn)];
}

/** Осталось дней до срока. `null`, если срока нет. Отрицательное — просрочено. */
export function daysLeft(deadline: DateLike, tz: string = displayTimeZone): number | null {
    const target = dayNumber(deadline);
    if (target === null) return null;
    return target - todayDayNumber(tz);
}

export type DeadlineState = 'ok' | 'soon' | 'late';

export function deadlineState(left: number | null | undefined): DeadlineState {
    if (left === null || left === undefined) return 'ok';
    if (left < 0) return 'late';
    if (left <= SOON_DAYS) return 'soon';
    return 'ok';
}

/**
 * CSS-модификатор для `.item-date` / `.my-date` / `.lr-date` / `.dr-date`:
 * пустая строка = базовый зелёный цвет «в срок».
 */
export function deadlineClass(left: number | null | undefined): '' | 'soon' | 'late' {
    const state = deadlineState(left);
    return state === 'ok' ? '' : state;
}

/**
 * Три варианта подписи срока (макет, `leftText`):
 * `31.08 (просрочено на 3 дня)` · `03.09 (сегодня)` · `08.09 (осталось 5 дней)`.
 */
export function leftText(
    deadline: DateLike,
    left?: number | null,
    tz: string = displayTimeZone,
): string {
    const short = fmtShort(deadline);
    if (!short) return '';
    const n = left === undefined || left === null ? daysLeft(deadline, tz) : left;
    if (n === null) return short;
    if (n < 0) {
        const overdue = Math.abs(n);
        return short + ' (просрочено на ' + overdue + ' ' + dayWord(overdue) + ')';
    }
    if (n === 0) return short + ' (сегодня)';
    return short + ' (осталось ' + n + ' ' + dayWord(n) + ')';
}

/** Уровень заливки ячейки календаря: `0 / ≤2 / ≤4 / >4`. */
export function calendarLevel(count: number): 0 | 1 | 2 | 3 {
    if (count === 0) return 0;
    if (count <= 2) return 1;
    if (count <= 4) return 2;
    return 3;
}

/* ─────────────────────────── Период ─────────────────────────── */

export type PeriodValue =
    | { mode: 'preset'; days: number }
    | { mode: 'custom'; from: string; to: string };

export interface PeriodParams {
    days?: number;
    from?: string;
    to?: string;
}

export const PERIOD_PRESETS = [1, 7, 30, 90] as const;

export const PERIOD_PRESET_LABELS: Readonly<Record<number, string>> = {
    1: 'Сутки',
    7: '7 дней',
    30: '30 дней',
    90: 'Квартал',
};

export const DEFAULT_PERIOD: PeriodValue = { mode: 'preset', days: 7 };

/** Нижняя граница произвольного периода (в макете — `HISTORY_START`). */
export const DEFAULT_HISTORY_START = '2025-01-01';

/** Параметры запроса: `?days=7` либо `?from=&to=`. */
export function periodToParams(period: PeriodValue): PeriodParams {
    return period.mode === 'custom' ? { from: period.from, to: period.to } : { days: period.days };
}

/** Стабильный ключ кэша для TanStack Query. */
export function periodKey(period: PeriodValue): string {
    return period.mode === 'custom'
        ? 'custom:' + period.from + ':' + period.to
        : 'days:' + period.days;
}

export function samePeriod(a: PeriodValue, b: PeriodValue): boolean {
    return periodKey(a) === periodKey(b);
}

/** `сутки` · `7 дней` · `квартал (90 дней)` · `01.09.2026 — 07.09.2026`. */
export function periodLabel(period: PeriodValue): string {
    if (period.mode === 'custom') return fmtDate(period.from) + ' — ' + fmtDate(period.to);
    if (period.days === 1) return 'сутки';
    if (period.days === 90) return 'квартал (90 дней)';
    return period.days + ' ' + dayWord(period.days);
}

export interface LocalRange {
    from: string;
    to: string;
    days: number;
    prev_from: string;
    prev_to: string;
}

/**
 * Тот же расчёт, что `computeRange()` в макете и на бэкенде: пресет заканчивается
 * сегодняшним днём включительно, предыдущий период той же длины стоит впритык.
 */
export function computeLocalRange(period: PeriodValue, tz: string = displayTimeZone): LocalRange {
    let fromDn: number;
    let toDn: number;
    if (period.mode === 'custom') {
        fromDn = dayNumber(period.from) ?? todayDayNumber(tz);
        toDn = dayNumber(period.to) ?? fromDn;
        if (toDn < fromDn) {
            const swap = fromDn;
            fromDn = toDn;
            toDn = swap;
        }
    } else {
        toDn = todayDayNumber(tz);
        fromDn = toDn - (Math.max(1, period.days) - 1);
    }
    const days = toDn - fromDn + 1;
    return {
        from: isoFromDayNumber(fromDn),
        to: isoFromDayNumber(toDn),
        days,
        prev_from: isoFromDayNumber(fromDn - days),
        prev_to: isoFromDayNumber(fromDn - 1),
    };
}

/** Разбор периода из query-параметров URL. */
export function parsePeriod(search: URLSearchParams): PeriodValue {
    const from = search.get('from');
    const to = search.get('to');
    if (from && to) return { mode: 'custom', from, to };
    const days = Number(search.get('days'));
    if (Number.isFinite(days) && days > 0) return { mode: 'preset', days };
    return DEFAULT_PERIOD;
}

/** Обратная операция: что положить в query-строку. */
export function periodToSearch(period: PeriodValue): Record<string, string> {
    return period.mode === 'custom'
        ? { from: period.from, to: period.to }
        : { days: String(period.days) };
}
